import type { IntlShape } from 'react-intl';

import { isString } from 'lodash';
import mapValues from 'lodash/mapValues';
import { v4 as uuid } from 'uuid';

import { isWellKnownFieldVisible } from '~/common/constants/wellKnownFields';
import {
  customSettingsKeys,
  customSettingsOptionValues,
  fieldTypesToDataFieldTypes,
  getTimePeriodFieldDefaultOptions,
  valueKeys,
} from '~/common/constants/workflow';
import { sortAndFilterWellKnownFieldIds } from '~/components/Shared/AppLayout/SidebarContent/IntakeForm/WorkflowForm/WellKnownFields/utils';
import { DateFormatType } from '~/enums/DateFormatType';
import { FileExtensionType } from '~/enums/FileExtensionType';
import { WorkflowFieldType } from '~/enums/WorkflowFieldType';
import { WorkflowIntakeFormType } from '~/enums/WorkflowIntakeFormType';
import { WorkflowPartyType } from '~/enums/WorkflowPartyType';
import { Workflow } from '~/types/Workflow';
import { coerceToArray } from '~/utils/array';
import { coerceFileType } from '~/utils/files';
import {
  capitalizeWords,
  isValueStrictlyEmpty,
  removeBreaklinesFromText,
  toLowercaseTrimmed,
} from '~/utils/strings';

type Dict<T = unknown> = Record<string, T>;

interface FieldValue {
  value?: unknown;
  [key: string]: unknown;
}

interface WellKnownFieldInput {
  id: string;
  name: string;
  type: WorkflowFieldType | string;
  customSettings?: Dict;
  value?: unknown;
  isEditable?: boolean;
  isRequired?: boolean;
}

interface NormalizedWellKnownField {
  id: string;
  name: string;
  type: WorkflowFieldType | string;
  customSettings: Dict;
  question: string;
  value: FieldValue;
  errorMessage: null;
  modified: boolean;
  isPlaceHolder: boolean;
  isEnabled: boolean;
  isTouched: boolean;
  isEditable: boolean;
  wellKnownKey: string;
}

interface IntakeFormQuestion {
  id: string;
  title: string;
  description: string | null;
  isReadOnly?: boolean;
  isRequired: boolean;
  position: number;
  field: {
    id: string;
    type: WorkflowFieldType | string;
    value?: FieldValue | null;
    customSettings: Dict & { defaultValue?: Dict };
    placeHolder?: unknown;
    [key: string]: unknown;
  };
}

interface IntakeFormSection {
  id: string;
  name: string;
  description: string | null;
  position: number;
  questions: IntakeFormQuestion[];
}

interface IntakeFormDataField {
  id: string;
  isEnabled?: boolean;
  isSignerField?: boolean;
  type?: WorkflowFieldType | string;
  value: FieldValue;
}

interface IntakeFormData {
  fields: Dict<IntakeFormDataField> | IntakeFormDataField[];
  [key: string]: unknown;
}

interface IntakeFormCondition {
  id: string;
  [key: string]: unknown;
}

interface IntakeFormResponse {
  conditions?: IntakeFormCondition[];
  sections: IntakeFormSection[];
  wellKnownFields?: Dict<WellKnownFieldInput> | null;
  ticket?: unknown;
  workflow?: Workflow;
  [key: string]: unknown;
}

interface DraftFieldDefinition {
  id: string;
  type?: WorkflowFieldType | string;
  placeholder?: string | null;
  conditionalText?: unknown;
  conditionRule?: DraftConditionRule | null;
  isImplicit?: boolean;
  isSignerField?: boolean;
  isEnabled?: boolean;
  isEsignatureTag?: boolean;
  staticKeyName?: string;
  name?: string;
  value?: FieldValue;
  [key: string]: unknown;
}

interface DraftConditionRule {
  id: string | null;
  satisfiedWhenCondMet?: boolean;
  enabledWhenSatisfied?: boolean;
}

interface DraftApproval {
  name?: string;
  [key: string]: unknown;
}

interface DraftSigner {
  id?: string;
  userId?: unknown;
  partyId: string;
  partyType?: WorkflowPartyType;
  [key: string]: unknown;
}

interface DraftPhase {
  title?: string;
  conditionRule?: DraftConditionRule | null;
  approvals: DraftApproval[];
  signers: DraftSigner[];
  [key: string]: unknown;
}

interface DraftStage {
  coordinator?: unknown;
  coordinatorId?: unknown;
  conditionRule?: DraftConditionRule | null;
  isEnabled?: boolean;
  phases: DraftPhase[];
  [key: string]: unknown;
}

interface DraftQuestion {
  id?: string;
  creating?: boolean;
  [key: string]: unknown;
}

interface DraftSection {
  name?: string;
  questions: DraftQuestion[];
  [key: string]: unknown;
}

interface DraftForm {
  sections: DraftSection[];
  [key: string]: unknown;
}

type DraftFieldLink = {
  id?: string | null;
  key?: string | null;
  entity?: string;
};

interface DraftWorkflow {
  fields: Dict<DraftFieldDefinition>;
  fieldLinks: Dict<DraftFieldLink>;
  form: DraftForm;
  conditions: Dict<DraftConditionRule>;
  stages: {
    sign: DraftStage;
    review: DraftStage;
    finalize: DraftStage;
    [key: string]: DraftStage;
  };
  signers: Dict<DraftSigner>;
  parties: Dict<{ type: WorkflowPartyType; [key: string]: unknown }>;
  settings: { dataFieldsMapping: Dict<unknown> };
  implicitFields: DraftFieldDefinition[];
  versions?: unknown;
  versionNumber?: number;
  acceptedFileTypes?: string[];
  [key: string]: unknown;
}

const unconventionalFields = [
  WorkflowFieldType.Address,
  WorkflowFieldType.MonetaryValue,
  WorkflowFieldType.TimePeriod,
];

function setInitialFieldValue(fieldType: WorkflowFieldType): Dict<null> {
  switch (fieldType) {
    case WorkflowFieldType.Address:
      return {
        [valueKeys[fieldType].COUNTRY]: null,
        [valueKeys[fieldType].STREET_1]: null,
        [valueKeys[fieldType].STREET_2]: null,
        [valueKeys[fieldType].CITY]: null,
        [valueKeys[fieldType].STATE]: null,
        [valueKeys[fieldType].ZIP_CODE]: null,
      };
    case WorkflowFieldType.MonetaryValue:
      return {
        [valueKeys[fieldType].AMOUNT]: null,
        [valueKeys[fieldType].CURRENCY]: null,
      };
    case WorkflowFieldType.TimePeriod:
      return {
        [valueKeys[fieldType].AMOUNT]: null,
        [valueKeys[fieldType].PERIOD]: null,
      };
    default:
      return { value: null };
  }
}

function normalizeWellKnownFieldValue(fieldValue: unknown): FieldValue {
  if (fieldValue === undefined || fieldValue === null) {
    return { value: null };
  }

  if (typeof fieldValue === 'object' && 'value' in fieldValue) {
    return fieldValue;
  }

  if (
    typeof fieldValue === 'object' &&
    ('default_value' in fieldValue || 'resource_id' in fieldValue)
  ) {
    return { value: fieldValue };
  }

  return { value: fieldValue };
}

function createWellKnownQuestion(field: WellKnownFieldInput) {
  const id = field.id;

  return {
    questionId: id,
    fieldId: id,
    question: {
      id,
      title: field.name,
      description: '',
      isReadOnly: field.isEditable === false,
      isRequired: field.isRequired || false,
      position: 0,
      field: id,
    },
  };
}

function createWellKnownField(
  fieldId: string,
  questionId: string,
  field: WellKnownFieldInput,
  wellKnownKey: string,
): NormalizedWellKnownField {
  return {
    id: fieldId,
    name: field.name,
    type: field.type,
    customSettings: field.customSettings || { options: [] },
    question: questionId,
    value: normalizeWellKnownFieldValue(field.value),
    errorMessage: null,
    modified: false,
    isPlaceHolder: false,
    isEnabled: true,
    isTouched: false,
    isEditable: field.isEditable !== false,
    wellKnownKey,
  };
}

function filterWellKnownFieldsByVisibility(
  wellKnownFields: Dict<WellKnownFieldInput> | null | undefined,
  _ticket: unknown,
  workflow: Workflow | undefined,
) {
  if (!wellKnownFields) {
    return wellKnownFields;
  }

  return Object.fromEntries(
    Object.entries(wellKnownFields).filter(([key]) =>
      isWellKnownFieldVisible(key, workflow),
    ),
  ) as Dict<WellKnownFieldInput>;
}

function normalizeWellKnownFields(
  wellKnownFields: Dict<WellKnownFieldInput> | null | undefined,
  shouldMergeIntoFields = false,
) {
  const result: {
    questionsForIntakeForm: Dict<
      ReturnType<typeof createWellKnownQuestion>['question']
    >;
    fieldsForIntakeForm: Dict<NormalizedWellKnownField>;
    wellKnownFieldsByKey: Dict<NormalizedWellKnownField>;
    sortedQuestionIds: string[];
  } = {
    questionsForIntakeForm: {},
    fieldsForIntakeForm: {},
    wellKnownFieldsByKey: {},
    sortedQuestionIds: [],
  };

  if (!wellKnownFields) {
    return result;
  }

  Object.entries(wellKnownFields).forEach(([key, field]) => {
    const { questionId, fieldId, question } = createWellKnownQuestion(field);
    const normalizedField = createWellKnownField(
      fieldId,
      questionId,
      field,
      key,
    );

    result.wellKnownFieldsByKey[key] = normalizedField;

    if (shouldMergeIntoFields) {
      result.questionsForIntakeForm[questionId] = question;
      result.fieldsForIntakeForm[fieldId] = normalizedField;
    }
  });

  if (
    shouldMergeIntoFields &&
    Object.keys(result.wellKnownFieldsByKey).length > 0
  ) {
    const sortedKeys = sortAndFilterWellKnownFieldIds(wellKnownFields);
    result.sortedQuestionIds = sortedKeys.map(
      (key) => result.wellKnownFieldsByKey[key].question,
    );
  }

  return result;
}

export function prefillMissingFieldOptions(
  customSettings: Dict & {
    enable_create_options?: boolean;
    options?: { label: string; value: string }[];
  },
  fieldValue: FieldValue,
) {
  if (customSettings.enable_create_options) {
    const options = [...(customSettings.options || [])];
    const optionValueSet = new Set(options.map((option) => option.value));
    const { value } = fieldValue;
    if (value) {
      const valueArray = coerceToArray(value as string | string[]);
      valueArray.forEach((v) => {
        if (!optionValueSet.has(v)) {
          options.push({
            label: v,
            value: v,
          });
        }
      });
    }
    return {
      ...customSettings,
      options,
    };
  } else {
    return customSettings;
  }
}

export function normalizeIntakeForm(
  response: IntakeFormResponse,
  isWSSIntegrationPhase2M1M3Enabled = false,
) {
  const normalizedSections: Dict<Dict> = {};
  const normalizedQuestions: Dict<Dict> = {};
  const normalizedFields: Dict<Dict> = {};

  const normalizedConditions: Dict<IntakeFormCondition> = (
    response.conditions ?? []
  ).reduce<Dict<IntakeFormCondition>>((acc, condition) => {
    acc[condition.id] = condition;
    return acc;
  }, {});

  const sections = [...response.sections];

  sections
    .sort((a, b) => a.position - b.position)
    .forEach((section) => {
      const { id, name, description, position, questions } = section;

      const questionsArr = [...questions];

      normalizedSections[id] = {
        ...{ id, name, description, position },
        questions: questionsArr
          .sort((a, b) => a.position - b.position)
          .map((question) => question.id),
      };

      questionsArr
        .sort((a, b) => a.position - b.position)
        .forEach((question) => {
          const {
            id,
            title,
            description,
            isReadOnly,
            isRequired,
            field,
            position,
          } = question;

          normalizedQuestions[id] = {
            ...{ id, title, description, isReadOnly, isRequired, position },
            field: field.id,
          };

          const {
            customSettings: { defaultValue },
          } = field;

          /*
            TODO: Should work with BE to make sure all field objects follow path `field.value.value` to get field values.
            * Also would require making some FE updates to address, monetary, and time period fields.
          */
          let fieldValue: FieldValue | Dict<null>;

          if (unconventionalFields.includes(field.type as WorkflowFieldType)) {
            fieldValue = field.value
              ? field.value
              : setInitialFieldValue(field.type as WorkflowFieldType);
          } else if (field.type === WorkflowFieldType.Number) {
            fieldValue =
              field.value && Number.isFinite(field.value.value as number)
                ? field.value
                : setInitialFieldValue(field.type as WorkflowFieldType);
          } else {
            const v = field.value?.value;
            const hasDefault = Object.keys(defaultValue || {}).length > 0;
            // Preserve the stored value when it's real content, or when it's
            // an empty/whitespace string AND a default exists — that
            // combination means "user explicitly cleared a pre-filled
            // default," which must not be re-stamped below. A cleared field
            // with no configured default (or a non-string empty value:
            // null/undefined/[]/{}) falls back to the initial value exactly
            // as before, so Select/display consumers never see an empty
            // string when there's nothing to protect against.
            const isExplicitlyClearedDefault =
              hasDefault && isString(v) && isValueStrictlyEmpty(v);
            fieldValue =
              field.value != null &&
              (isExplicitlyClearedDefault || !isValueStrictlyEmpty(v))
                ? field.value
                : setInitialFieldValue(field.type as WorkflowFieldType);
          }

          if (defaultValue) {
            Object.keys(defaultValue).forEach((key) => {
              if (fieldValue[key] === null) {
                fieldValue[key] = defaultValue[key];
              }
            });
          }

          const customSettings = prefillMissingFieldOptions(
            field.customSettings,
            fieldValue,
          );

          normalizedFields[field.id] = {
            ...field,
            customSettings,
            question: question.id,
            value: fieldValue,
            errorMessage: null,
            modified: false,
            isPlaceHolder: field?.placeHolder != null,
          };
        });
    });

  const filteredWellKnownFields = filterWellKnownFieldsByVisibility(
    response.wellKnownFields,
    response.ticket,
    response.workflow,
  );

  const wellKnownFieldsResult = normalizeWellKnownFields(
    filteredWellKnownFields,
    isWSSIntegrationPhase2M1M3Enabled,
  );

  if (
    isWSSIntegrationPhase2M1M3Enabled &&
    wellKnownFieldsResult.sortedQuestionIds.length > 0
  ) {
    normalizedSections['well-known-fields'] = {
      id: 'well-known-fields',
      name: 'Procurement Contract and Terms Information',
      description: '',
      position: -1,
      questions: wellKnownFieldsResult.sortedQuestionIds,
    };
  }

  return {
    ...response,
    conditions: normalizedConditions,
    sections: normalizedSections,
    questions: {
      ...normalizedQuestions,
      ...wellKnownFieldsResult.questionsForIntakeForm,
    },
    fields: {
      ...normalizedFields,
      ...wellKnownFieldsResult.fieldsForIntakeForm,
    },
    wellKnownFields: filteredWellKnownFields,
    isValidated: false,
  };
}

export function prepareIntakeFormSubmitData(formData: IntakeFormData) {
  const fields = Object.values(formData.fields)
    .filter((field) => field.isEnabled)
    .map((field) => ({
      fieldId: field.id,
      value: mapValues(field.value, (v) => isEmptyValue(v)),
    }))
    .concat(clearDisabledEmailSignerFields(formData));

  return { ...formData, fields };
}

export function clearDisabledEmailSignerFields(formData: IntakeFormData) {
  return Object.values(formData.fields)
    .filter(
      (field) =>
        field.isSignerField &&
        field.type === WorkflowFieldType.Email &&
        !field.isEnabled,
    )
    .map((field) => ({
      fieldId: field.id,
      value: mapValues(field.value, () => ''),
    }));
}

function isEmptyValue(value: unknown): string | number | boolean | null {
  if (value === '' || value === null || value === undefined) {
    return '';
  } else {
    if (isString(value)) {
      return removeBreaklinesFromText(value);
    }
    return value as string | number | boolean | null;
  }
}

export function coerceName(
  name: string | null | undefined,
  entityName: string,
  index: number,
) {
  return name || `${entityName} ${index + 1}`;
}

export function testConditionalTextField(field: DraftFieldDefinition) {
  return field.conditionalText;
}

export function testContractField(
  field: DraftFieldDefinition,
  fieldLinks: Dict<{ entity?: string }> = {},
) {
  return (
    !testIsWSSContractField(field) &&
    field.type &&
    ['contract', 'custom'].includes(fieldLinks[field.id]?.entity ?? '')
  );
}
export function testIsWSSContractField(
  field: DraftFieldDefinition | null | undefined,
) {
  return !!(field?.type && field.staticKeyName);
}

export function testCustomField(field: DraftFieldDefinition) {
  return !field.placeholder;
}

export function testImplicitField(field: DraftFieldDefinition) {
  return field.isImplicit;
}

export function testDocumentField(field: DraftFieldDefinition) {
  return field.placeholder && !field.conditionalText;
}

export function testUnassignedField(field: DraftFieldDefinition) {
  return field.placeholder && !field.type;
}

export function testInvalidConditionRule(
  conditionRule: DraftConditionRule | null | undefined,
  conditions: Dict<DraftConditionRule>,
) {
  if (!!conditionRule) {
    const conditionId = conditionRule.id;
    return !conditionId || !conditions[conditionId];
  }
  return false;
}

export function testInvalidFieldMapping(
  field: DraftFieldDefinition,
  builder: { fieldSections: Dict<{ fieldType?: unknown }> },
  settings: { dataFieldsMapping: Dict<unknown> },
) {
  const allowedTypes =
    fieldTypesToDataFieldTypes[
      field.type as keyof typeof fieldTypesToDataFieldTypes
    ] ?? [];
  const mappedFieldSectionId = settings.dataFieldsMapping[field.id];
  // if there are no mapped field for this field id, then it is valid (e.g return false);
  // if the mappedSectionId === 0, then it's an imported field, we should validate it
  if (mappedFieldSectionId !== 0 && !mappedFieldSectionId) return false;
  const mappedFieldSection =
    builder.fieldSections[mappedFieldSectionId as string];
  return !(allowedTypes as unknown[]).includes(mappedFieldSection?.fieldType);
}

function testIsStageDisabledWithoutCondition(
  isEnabled: boolean | undefined,
  conditionRule: DraftConditionRule | null | undefined,
) {
  return !(isEnabled || conditionRule);
}

export function testShouldEvaluateStageConditions(
  isEnabled: boolean | undefined,
  conditionRule: DraftConditionRule | null | undefined,
) {
  return (
    isEnabled || !testIsStageDisabledWithoutCondition(isEnabled, conditionRule)
  );
}

export function testIsStageEnabledOrIsDisabledWithCondition(
  isEnabled: boolean | undefined,
  conditionRule: DraftConditionRule | null | undefined,
) {
  return isEnabled || (!isEnabled && conditionRule);
}

export function testInvalidSigner(
  signer: DraftSigner | null | undefined,
  signers: Dict<DraftSigner>,
) {
  if (!!signer) {
    const signerId = signer.id;
    return !signerId || !signers[signerId];
  }
}

export function testESignatureField(field: DraftFieldDefinition) {
  return field.isEsignatureTag;
}

export function getPartyName(
  type: WorkflowPartyType | WorkflowIntakeFormType,
  index: number,
) {
  return coerceName(
    null,
    // i18n-ignore No access to intl context
    type === WorkflowPartyType.Counterparty ? 'Counterparty' : 'Internal Party',
    index,
  );
}

interface FieldDefinitionLike {
  entity?: string;
  key?: string;
}

// determines if a provided fieldDefinition isSelected or isUsed based on other contextual data (entityId, fieldLinks, selectedFieldDefinition)
export function getFieldDefinitionStatus({
  entityId,
  fieldDefinition,
  fieldLinks,
  selectedEntityId,
  selectedFieldDefinition,
  intl,
}: {
  entityId?: string | null;
  fieldDefinition?: FieldDefinitionLike | null;
  fieldLinks: Dict<FieldDefinitionLike & { id?: string }>;
  selectedEntityId?: string | null;
  selectedFieldDefinition?: FieldDefinitionLike | null;
  intl: IntlShape;
}) {
  const fieldLinkValues = Object.values(fieldLinks);
  const { entity: fieldDefinitionEntity, key: fieldDefinitionKey } =
    fieldDefinition || {};

  let isSelected = false;
  let isUsed = false;
  let tooltip: string | undefined;

  if (entityId) {
    isSelected = entityId === selectedEntityId;
  } else if (selectedFieldDefinition) {
    isSelected =
      selectedFieldDefinition.entity === fieldDefinitionEntity &&
      selectedFieldDefinition.key === fieldDefinitionKey;
  }

  isUsed = fieldLinkValues.some((fieldLink) => {
    let matches =
      fieldLink.entity === fieldDefinitionEntity &&
      fieldLink.key === fieldDefinitionKey;
    if (entityId) {
      matches = matches && entityId === fieldLink.id;
    }
    return matches;
  });

  if (isUsed) {
    tooltip = intl.formatMessage({
      id: 'Workflow.fieldAlreadyAssigned',
      defaultMessage: 'This field has already been assigned',
      description:
        'Tooltip message when a field is already assigned to another element',
    });
  }

  return {
    isSelected,
    isUsed,
    tooltip,
  };
}

/* ----------------------------------------------------
 *  Workflow Builder: Field Validator
 *  ----------------------------------------------------
 */

export function testInvalidField(
  fieldId: string | null | undefined,
  fields: Dict<unknown>,
  fieldLinks: Dict<unknown>,
) {
  const doesFieldExist = !!(fieldId && fields[fieldId]);
  const doesFieldLinkExist = !!(fieldId && fieldLinks[fieldId]);
  return !doesFieldExist || !doesFieldLinkExist;
}

/* ----------------------------------------------------
 *  Workflow Builder: Permissions
 *  ----------------------------------------------------
 */
export const creatorPermissionEnums = {
  EDIT_VIEW: 'EDIT_VIEW',
  REVIEW_VIEW: 'REVIEW_VIEW',
  SIGN_VIEW: 'SIGN_VIEW',
  FINALIZE_VIEW: 'FINALIZE_VIEW',
  COMPLETED_VIEW: 'COMPLETED_VIEW',
  TICKET_VIEW: 'TICKET_VIEW',

  VIEW_APPROVAL_BASIC: 'VIEW_APPROVAL_BASIC',
  VIEW_APPROVAL_DETAILED: 'VIEW_APPROVAL_DETAILED',

  ACTIVITY_LOG: 'ACTIVITY_LOG',
  REASSIGN_ANY: 'REASSIGN_ANY',
  SEND_FOR_REVIEW: 'SEND_FOR_REVIEW',
  SEND_FOR_SIGNATURES: 'SEND_FOR_SIGNATURES',
  SEND_FOR_FINALIZATION: 'SEND_FOR_FINALIZATION',
  MARK_COMPLETE: 'MARK_COMPLETE',

  EDIT_FORM: 'EDIT_FORM',
  UPLOAD_VERSION: 'UPLOAD_VERSION',
  REVIEWER: 'REVIEWER',
  DOWNLOAD: 'DOWNLOAD',
  SHARE: 'SHARE',
  EDIT_PARTICIPANTS: 'EDIT_PARTICIPANTS',

  UPLOAD_SIGNED: 'UPLOAD_SIGNED',
  COLLECT_SIGNATURES: 'COLLECT_SIGNATURES',
  APPROVE_ANY: 'APPROVE_ANY',
  REJECT_ANY: 'REJECT_ANY',
  RESET_ANY: 'RESET_ANY',
};

export const getEditOptions = (intl: IntlShape) => [
  {
    value: creatorPermissionEnums.EDIT_VIEW,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.editView',
      defaultMessage: 'View edit stage',
      description: 'Permission to view the edit stage of a workflow',
    }),
  },
  {
    value: creatorPermissionEnums.SEND_FOR_REVIEW,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.moveToReviewStage',
      defaultMessage: 'Move to Review Stage',
      description: 'Permission to move workflow to review stage',
    }),
  },
  {
    value: creatorPermissionEnums.REASSIGN_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.reassign',
      defaultMessage: 'Reassign',
      description: 'Permission to reassign workflow',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_FORM,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.editFormInformation',
      defaultMessage: 'Edit form information',
      description: 'Permission to edit form information',
    }),
  },
  {
    value: creatorPermissionEnums.UPLOAD_VERSION,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.uploadNewVersion',
      defaultMessage: 'Upload new version',
      description: 'Permission to upload new version of document',
    }),
  },
  {
    value: creatorPermissionEnums.REVIEWER,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewDocument',
      defaultMessage: 'View document',
      description: 'Permission to view document',
    }),
  },
  {
    value: creatorPermissionEnums.DOWNLOAD,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.downloadDocument',
      defaultMessage: 'Download document',
      description: 'Permission to download document',
    }),
  },
  {
    value: creatorPermissionEnums.SHARE,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.shareDocument',
      defaultMessage: 'Share document',
      description: 'Permission to share document',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_PARTICIPANTS,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.addParticipants',
      defaultMessage: 'Add participants',
      description: 'Permission to add participants to workflow',
    }),
  },
];

export const getReviewOptions = (intl: IntlShape) => [
  {
    value: creatorPermissionEnums.REVIEW_VIEW,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.reviewView',
      defaultMessage: 'View review stage',
      description: 'Permission to view the review stage of a workflow',
    }),
  },
  {
    value: creatorPermissionEnums.SEND_FOR_SIGNATURES,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.moveToSignStage',
      defaultMessage: 'Move to Sign Stage',
      description: 'Permission to move workflow to sign stage',
    }),
  },
  {
    value: creatorPermissionEnums.VIEW_APPROVAL_BASIC,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewApprovalBasic',
      defaultMessage: "View only stage and who's approving/has to approve",
      description: 'Permission to view basic approval information',
    }),
  },
  {
    value: creatorPermissionEnums.VIEW_APPROVAL_DETAILED,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewApprovalDetailedReview',
      defaultMessage: 'View all details of approval in Review Phase',
      description:
        'Permission to view detailed approval information in review phase',
    }),
  },
  {
    value: creatorPermissionEnums.REASSIGN_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.reassign',
      defaultMessage: 'Reassign',
      description: 'Permission to reassign workflow',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_FORM,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.editFormInformation',
      defaultMessage: 'Edit form information',
      description: 'Permission to edit form information',
    }),
  },
  {
    value: creatorPermissionEnums.UPLOAD_VERSION,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.uploadNewVersion',
      defaultMessage: 'Upload new version',
      description: 'Permission to upload new version of document',
    }),
  },
  {
    value: creatorPermissionEnums.REVIEWER,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewDocument',
      defaultMessage: 'View document',
      description: 'Permission to view document',
    }),
  },
  {
    value: creatorPermissionEnums.DOWNLOAD,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.downloadDocument',
      defaultMessage: 'Download document',
      description: 'Permission to download document',
    }),
  },
  {
    value: creatorPermissionEnums.SHARE,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.shareDocument',
      defaultMessage: 'Share document',
      description: 'Permission to share document',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_PARTICIPANTS,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.addParticipants',
      defaultMessage: 'Add participants',
      description: 'Permission to add participants to workflow',
    }),
  },
  {
    value: creatorPermissionEnums.APPROVE_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.approveApprovals',
      defaultMessage: 'Approve Approvals',
      description: 'Permission to approve approvals',
    }),
  },
  {
    value: creatorPermissionEnums.REJECT_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.rejectApprovals',
      defaultMessage: 'Reject Approvals',
      description: 'Permission to reject approvals',
    }),
  },
  {
    value: creatorPermissionEnums.RESET_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.resetApprovals',
      defaultMessage: 'Reset Approvals',
      description: 'Permission to reset approvals',
    }),
  },
];

export const getSignOptions = (intl: IntlShape) => [
  {
    value: creatorPermissionEnums.SIGN_VIEW,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.signView',
      defaultMessage: 'View sign stage',
      description: 'Permission to view the sign stage of a workflow',
    }),
  },
  {
    value: creatorPermissionEnums.SEND_FOR_FINALIZATION,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.moveToFinalizeStage',
      defaultMessage: 'Move to Finalize Stage',
      description: 'Permission to move workflow to finalize stage',
    }),
  },
  {
    value: creatorPermissionEnums.VIEW_APPROVAL_BASIC,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewApprovalBasic',
      defaultMessage: "View only stage and who's approving/has to approve",
      description: 'Permission to view basic approval information',
    }),
  },
  {
    value: creatorPermissionEnums.VIEW_APPROVAL_DETAILED,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewApprovalDetailedSigning',
      defaultMessage: 'View all details of approval in Signing Phase',
      description:
        'Permission to view detailed approval information in signing phase',
    }),
  },
  {
    value: creatorPermissionEnums.REASSIGN_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.reassign',
      defaultMessage: 'Reassign',
      description: 'Permission to reassign workflow',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_FORM,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.editFormInformation',
      defaultMessage: 'Edit form information',
      description: 'Permission to edit form information',
    }),
  },
  {
    value: creatorPermissionEnums.UPLOAD_VERSION,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.uploadNewVersion',
      defaultMessage: 'Upload new version',
      description: 'Permission to upload new version of document',
    }),
  },
  {
    value: creatorPermissionEnums.REVIEWER,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewDocument',
      defaultMessage: 'View document',
      description: 'Permission to view document',
    }),
  },
  {
    value: creatorPermissionEnums.DOWNLOAD,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.downloadDocument',
      defaultMessage: 'Download document',
      description: 'Permission to download document',
    }),
  },
  {
    value: creatorPermissionEnums.SHARE,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.shareDocument',
      defaultMessage: 'Share document',
      description: 'Permission to share document',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_PARTICIPANTS,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.addParticipants',
      defaultMessage: 'Add participants',
      description: 'Permission to add participants to workflow',
    }),
  },
  {
    value: creatorPermissionEnums.UPLOAD_SIGNED,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.uploadSignedDocument',
      defaultMessage: 'Upload signed document',
      description: 'Permission to upload signed document',
    }),
  },
  {
    value: creatorPermissionEnums.COLLECT_SIGNATURES,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.collectSignatures',
      defaultMessage: 'Collect Signatures',
      description: 'Permission to collect signatures',
    }),
  },
  {
    value: creatorPermissionEnums.RESET_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.resetSignatures',
      defaultMessage: 'Reset signatures',
      description: 'Permission to reset signatures',
    }),
  },
];

export const getFinalizeOptions = (intl: IntlShape) => [
  {
    value: creatorPermissionEnums.FINALIZE_VIEW,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.finalizeView',
      defaultMessage: 'View finalize stage',
      description: 'Permission to view the finalize stage of a workflow',
    }),
  },
  {
    value: creatorPermissionEnums.COMPLETED_VIEW,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewFinalizedTicket',
      defaultMessage: 'View finalized ticket',
      description: 'Permission to view finalized ticket',
    }),
  },
  {
    value: creatorPermissionEnums.MARK_COMPLETE,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.markAsComplete',
      defaultMessage: 'Mark as Complete',
      description: 'Permission to mark workflow as complete',
    }),
  },
  {
    value: creatorPermissionEnums.VIEW_APPROVAL_BASIC,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewApprovalBasic',
      defaultMessage: "View only stage and who's approving/has to approve",
      description: 'Permission to view basic approval information',
    }),
  },
  {
    value: creatorPermissionEnums.VIEW_APPROVAL_DETAILED,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewApprovalDetailedFinalize',
      defaultMessage: 'View all details of approval in Finalize Phase',
      description:
        'Permission to view detailed approval information in finalize phase',
    }),
  },
  {
    value: creatorPermissionEnums.REASSIGN_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.reassign',
      defaultMessage: 'Reassign',
      description: 'Permission to reassign workflow',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_FORM,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.editFormInformation',
      defaultMessage: 'Edit form information',
      description: 'Permission to edit form information',
    }),
  },
  {
    value: creatorPermissionEnums.UPLOAD_VERSION,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.uploadNewVersion',
      defaultMessage: 'Upload new version',
      description: 'Permission to upload new version of document',
    }),
  },
  {
    value: creatorPermissionEnums.REVIEWER,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.viewDocument',
      defaultMessage: 'View document',
      description: 'Permission to view document',
    }),
  },
  {
    value: creatorPermissionEnums.DOWNLOAD,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.downloadDocument',
      defaultMessage: 'Download document',
      description: 'Permission to download document',
    }),
  },
  {
    value: creatorPermissionEnums.SHARE,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.shareDocument',
      defaultMessage: 'Share document',
      description: 'Permission to share document',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_PARTICIPANTS,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.addParticipants',
      defaultMessage: 'Add participants',
      description: 'Permission to add participants to workflow',
    }),
  },
  {
    value: creatorPermissionEnums.RESET_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.resetTasksFinalize',
      defaultMessage: 'Reset Tasks (finalize)',
      description: 'Permission to reset tasks in finalize stage',
    }),
  },
  {
    value: creatorPermissionEnums.APPROVE_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.approveFinalization',
      defaultMessage: 'Approve Finalization',
      description: 'Permission to approve finalization',
    }),
  },
  {
    value: creatorPermissionEnums.REJECT_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.rejectFinalization',
      defaultMessage: 'Reject Finalization',
      description: 'Permission to reject finalization',
    }),
  },
];

export const getTicketOptions = (intl: IntlShape) => [
  {
    value: creatorPermissionEnums.TICKET_VIEW,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.ticketView',
      defaultMessage: 'View Ticket',
      description: 'Permission to view ticket',
    }),
  },
  {
    value: creatorPermissionEnums.REASSIGN_ANY,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.reassign',
      defaultMessage: 'Reassign',
      description: 'Permission to reassign workflow',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_FORM,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.editFormInformation',
      defaultMessage: 'Edit form information',
      description: 'Permission to edit form information',
    }),
  },
  {
    value: creatorPermissionEnums.UPLOAD_VERSION,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.uploadNewVersion',
      defaultMessage: 'Upload new version',
      description: 'Permission to upload new version of document',
    }),
  },
  {
    value: creatorPermissionEnums.DOWNLOAD,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.downloadDocument',
      defaultMessage: 'Download document',
      description: 'Permission to download document',
    }),
  },
  {
    value: creatorPermissionEnums.SHARE,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.shareDocument',
      defaultMessage: 'Share document',
      description: 'Permission to share document',
    }),
  },
  {
    value: creatorPermissionEnums.EDIT_PARTICIPANTS,
    label: intl.formatMessage({
      id: 'WorkflowBuilder.addParticipants',
      defaultMessage: 'Add participants',
      description: 'Permission to add participants to workflow',
    }),
  },
];

export function mergeImplicitFields(workflow: DraftWorkflow) {
  const fields = workflow.fields;
  workflow.implicitFields.forEach((implicitField) => {
    const { name, customSettings, description, type } = implicitField;
    fields[name as string] = {
      id: name as string,
      name,
      customSettings,
      description,
      isImplicit: true,
      type,
    };
  });
  return {
    ...workflow,
    fields,
  };
}

function dropImplicitFields(workflow: DraftWorkflow) {
  const fields = Object.values(workflow.fields).reduce<
    Dict<DraftFieldDefinition>
  >((acc, field) => {
    if (!testImplicitField(field)) {
      acc[field.id] = field;
    }
    return acc;
  }, {});
  return {
    ...workflow,
    fields,
  };
}

function dropUnsavedQuestions(workflow: DraftWorkflow) {
  const form = workflow.form;

  const sections = form.sections.map((section) => {
    return {
      ...section,
      questions: section.questions.filter((question) => !question.creating),
    };
  });

  return {
    ...workflow,
    form: {
      ...form,
      sections,
    },
  };
}

interface ConditionRuleHolder {
  conditionRule?: DraftConditionRule | null;
  [key: string]: unknown;
}

function dropUnspecifiedConditionRules(workflow: DraftWorkflow) {
  const { conditions, form, stages } = workflow;
  const { sign, review, finalize } = stages;

  function dropConditionRule(
    sections: Dict<ConditionRuleHolder[]>[],
    itemKey: string,
  ) {
    return sections.map((section) => {
      return {
        ...section,
        [itemKey]: section[itemKey].map((item) => {
          const { conditionRule } = item;
          return {
            ...item,
            conditionRule: testInvalidConditionRule(conditionRule, conditions)
              ? null
              : conditionRule,
          };
        }),
      };
    });
  }

  const fields = Object.values(workflow.fields).reduce<
    Dict<DraftFieldDefinition>
  >((acc, field) => {
    acc[field.id] = field;
    if (
      testConditionalTextField(field) &&
      testInvalidConditionRule(field.conditionRule, conditions)
    ) {
      acc[field.id] = {
        ...field,
        conditionRule: null,
      };
    }
    return acc;
  }, {});

  return {
    ...workflow,
    fields,
    form: {
      ...form,
      sections: dropConditionRule(
        form.sections as unknown as Dict<ConditionRuleHolder[]>[],
        'questions',
      ),
    },
    stages: {
      ...stages,
      sign: {
        ...sign,
        conditionRule: testInvalidConditionRule(sign.conditionRule, conditions)
          ? null
          : sign.conditionRule,
        phases: dropConditionRule(
          sign.phases as unknown as Dict<ConditionRuleHolder[]>[],
          'signers',
        ),
      },
      review: {
        ...review,
        conditionRule: testInvalidConditionRule(
          review.conditionRule,
          conditions,
        )
          ? null
          : review.conditionRule,
        phases: dropConditionRule(
          review.phases as unknown as Dict<ConditionRuleHolder[]>[],
          'approvals',
        ),
      },
      finalize: {
        ...finalize,
        conditionRule: testInvalidConditionRule(
          finalize.conditionRule,
          conditions,
        )
          ? null
          : finalize.conditionRule,
        phases: dropConditionRule(
          finalize.phases as unknown as Dict<ConditionRuleHolder[]>[],
          'approvals',
        ),
      },
    },
  } as unknown as DraftWorkflow;
}

function dropUnsavedSigners(workflow: DraftWorkflow) {
  const { sign } = workflow.stages;
  const signStage = {
    ...sign,
    phases: sign.phases.map((phase) => ({
      ...phase,
      signers: (phase.signers ?? []).filter((signer) => signer.id),
    })),
  };
  return {
    ...workflow,
    stages: {
      ...workflow.stages,
      sign: signStage,
    },
  };
}

function dropVersions(workflow: DraftWorkflow) {
  const workflowWithoutVersions = { ...workflow };
  delete workflowWithoutVersions.versions;
  return workflowWithoutVersions;
}

function dropInvalidDataFieldsMapping(workflow: DraftWorkflow) {
  const dataFieldsMapping = workflow.settings.dataFieldsMapping;
  const validDataFieldsMapping: Dict<unknown> = {};
  Object.keys(dataFieldsMapping).forEach(function (key) {
    if (dataFieldsMapping[key] !== null && workflow.fields[key])
      validDataFieldsMapping[key] = dataFieldsMapping[key];
  });

  return {
    ...workflow,
    settings: {
      ...workflow.settings,
      dataFieldsMapping: validDataFieldsMapping,
    },
  };
}

// This method will be deprecated and it exists only to aid in refactoring the API.
export const DEPRECATED_coerceSigners = (workflow: DraftWorkflow) => {
  return {
    ...workflow,
    signers: Object.fromEntries(
      Object.entries(workflow.signers).map(([signerId, signer]) => [
        signerId,
        { ...signer, userId: signer.userId || null },
      ]),
    ),
  };
};

// This method will be deprecated and it exists only to aid in refactoring the API.
const DEPRECATED_dropNoneRoleBasedAttributes = (workflow: DraftWorkflow) => {
  const review = { ...workflow.stages.review };
  const sign = { ...workflow.stages.sign };
  const finalize = { ...workflow.stages.finalize };
  delete review.coordinator;
  delete review.coordinatorId;
  delete sign.coordinator;
  delete sign.coordinatorId;
  delete finalize.coordinator;
  delete finalize.coordinatorId;
  return {
    ...workflow,
    signers: Object.entries(workflow.signers).reduce<Dict<DraftSigner>>(
      (acc, [signerId, signer]) => {
        const { userId: _unused, ...signerWithoutUserIdAttribute } = signer;
        acc[signerId] = signerWithoutUserIdAttribute;
        return acc;
      },
      {},
    ),
    stages: {
      ...workflow.stages,
      review,
      sign,
      finalize,
    },
  };
};

// TODO: The backend should handle this and ideally store the UI state as is.  This allows the UI to be reproduced faithfully when users revist where they left off.  In addition, it will also take advantage of the UI error checking which currently highlights these errors in the UI, but is now dropping the values out when we save a draft because the backend does not accept these situations.  To the user, they should be able to save a draft (with errors), and the UI is able to indicate them to areas with errors.  The errors should not disable saving the draft, but should only disable publishing the workflow.  The current code here is a compromise of scope that the FE is helping cover for the BE.
export function cleanupWorkflow(
  workflow: DraftWorkflow,
  shouldPublish = false,
) {
  workflow = dropInvalidDataFieldsMapping(workflow);
  workflow = dropImplicitFields(workflow);
  workflow = dropUnsavedQuestions(workflow);
  workflow = dropUnsavedSigners(workflow);
  workflow = dropUnspecifiedConditionRules(workflow);
  workflow = dropVersions(workflow);
  workflow = DEPRECATED_coerceSigners(workflow);
  workflow = DEPRECATED_dropNoneRoleBasedAttributes(workflow);

  if (shouldPublish) {
    workflow = coerceEmptyWorkflowValues(workflow);
  }
  return workflow;
}

// if users have left out empty values, coerce these to meaningful values consistent to what is displayed on the UI.  Should be used before publishing.
export function coerceEmptyWorkflowValues(workflow: DraftWorkflow) {
  const { form } = workflow;
  const { finalize, review, sign } = workflow.stages;

  // i18n-ignore No access to intl context

  form.sections.forEach((section, sectionIndex) => {
    section.name = coerceName(section.name, 'Form Section', sectionIndex);
  });
  finalize.phases.forEach((phase, phaseIndex) => {
    phase.title = coerceName(phase.title, 'Finalize Phase', phaseIndex);
    phase.approvals.forEach((approval, approvalIndex) => {
      approval.name = coerceName(approval.name, 'Finalize Task', approvalIndex);
    });
  });
  review.phases.forEach((phase, phaseIndex) => {
    phase.title = coerceName(phase.title, 'Review Phase', phaseIndex);
    phase.approvals.forEach((approval, approvalIndex) => {
      approval.name = coerceName(approval.name, 'Review Task', approvalIndex);
    });
  });
  sign.phases.forEach((phase, phaseIndex) => {
    phase.title = coerceName(phase.title, 'Signing Phase', phaseIndex);
    const newApprovals = phase.approvals.map((approval, approvalIndex) => ({
      ...approval,
      name: coerceName(approval.name, 'Signer', approvalIndex),
    }));
    phase.approvals = newApprovals;
  });
  return workflow;
}

export function createPartiesSigners() {
  const parties: Dict<{ id: string; name: string; type: WorkflowPartyType }> =
    {};
  const signers: Dict<{
    id: string;
    name: string;
    partyId: string;
    index: number;
  }> = {};
  const newParty = createParty({
    type: WorkflowPartyType.Party,
    name: 'Internal Party 1',
  });
  const newParty2 = createParty({
    type: WorkflowPartyType.Counterparty,
    name: 'Counterparty 1',
  });
  const newSigner = {
    id: uuid(),
    name: 'Signer 1',
    partyId: newParty.id,
    index: 0,
  };
  const newSigner2 = {
    id: uuid(),
    name: 'Signer 2',
    partyId: newParty2.id,
    index: 1,
  };

  parties[newParty.id] = newParty;
  parties[newParty2.id] = newParty2;
  signers[newSigner.id] = newSigner;
  signers[newSigner2.id] = newSigner2;
  return {
    parties,
    signers,
  };
}

function createParty({
  type,
  name,
}: {
  type: WorkflowPartyType;
  name: string;
}) {
  return { id: uuid(), name, type };
}

export function getFieldIdsByTrimmedKey(fields: Dict<Dict>, labelKey: string) {
  return Object.values(fields).reduce<Dict<string[]>>((acc, field) => {
    if (field[labelKey]) {
      const trimmed = toLowercaseTrimmed(field[labelKey] as string);
      if (!acc[trimmed]) {
        acc[trimmed] = [];
      }
      acc[trimmed].push(field.id as string);
    }
    return acc;
  }, {});
}

export function testIsFieldNameUsed(
  fields: Dict<Dict>,
  name: string,
  excludedName?: string,
) {
  const fieldIdsByFieldName = getFieldIdsByTrimmedKey(fields, 'name');
  const isFieldNameUsed =
    (fieldIdsByFieldName[toLowercaseTrimmed(name)]?.length ?? 0) > 0;
  return excludedName
    ? toLowercaseTrimmed(excludedName) !== toLowercaseTrimmed(name) &&
        isFieldNameUsed
    : isFieldNameUsed;
}

export function createFieldNameFromPlaceholder(placeholder: string) {
  return capitalizeWords(placeholder.split('_').join(' '));
}

/**
 * Business logic:
  fields    | internal signer    | external signer
  name      | not required       | required
  email     | not                | required
  title     | not                | not
  signature | when company paper | when company paper
  sign date | not                | not
*/
export function testSignerFieldDefinitionRequired(
  signer: { partyType?: WorkflowPartyType },
  fieldDefinition: { entity?: string; key?: string },
  workflowType: WorkflowIntakeFormType,
) {
  const { entity, key } = fieldDefinition;

  if (entity !== 'signer') {
    return false;
  }

  const isSignatureRequired =
    [
      WorkflowIntakeFormType.Company,
      WorkflowIntakeFormType.CompanyAndCounterparty,
    ].includes(workflowType) && key === 'signature';

  switch (signer.partyType) {
    case WorkflowPartyType.Party:
      return isSignatureRequired;
    case WorkflowPartyType.Counterparty:
      return ['name', 'email'].includes(key ?? '') || isSignatureRequired;
    default:
      return false;
  }
}

/**
 * Merge fields and fieldLinks based on the following requirements:
 *
 * - Keep all custom fields as-is.
 * - Keep placeholder fields/fieldLinks if they match new placeholder fields.
 * - Remove placeholder fields/fieldLinks for entries not found in newFields.
 */

export function mergeFields(
  newFields: Dict<DraftFieldDefinition>,
  fields: Dict<DraftFieldDefinition>,
  fieldLinks: Dict<DraftFieldLink>,
): [Dict<DraftFieldDefinition>, Dict<DraftFieldLink>] {
  // keep all properties of old field and selectively overwrite special fields (e.g. conditionalText);
  function mergeField(
    newField: Partial<DraftFieldDefinition> = {},
    oldField: DraftFieldDefinition,
  ) {
    const mergedField = { ...oldField };
    mergedField.conditionalText = newField.conditionalText;
    // only when old field type is CONDITONAL_TEXT, is it always safe to rewrite with the newField.type
    if (oldField.type === WorkflowFieldType.ConditionalText) {
      mergedField.type = newField.type;
    } else {
      mergedField.type = newField.type || oldField.type;
    }
    return mergedField;
  }
  const newPlaceholderFieldsMapping = Object.values(newFields).reduce<
    Dict<DraftFieldDefinition[]>
  >((acc, field) => {
    const { placeholder } = field;
    if (placeholder) {
      if (!acc[placeholder]) {
        acc[placeholder] = [];
      }
      acc[placeholder].push(field);
    }
    return acc;
  }, {});

  const merged = Object.values(fields).reduce<
    [Dict<DraftFieldDefinition>, Dict<DraftFieldLink>]
  >(
    ([mergedFields, mergedFieldLinks], field) => {
      const { id, placeholder } = field;
      const matchingFields =
        newPlaceholderFieldsMapping[placeholder ?? ''] || [];
      const hasMatchingPlaceholder = matchingFields.length > 0;
      const matchingField = matchingFields[0] || {};
      // keep placeholder fields and fieldLinks that match new placeholder fields
      if (testCustomField(field) || hasMatchingPlaceholder) {
        const mergedField = mergeField(matchingField, field);
        mergedFields[id] = mergedField;
        // if a field is converted to a conditional text field, drop its previous link.
        if (fieldLinks[id] && !testConditionalTextField(mergedField)) {
          mergedFieldLinks[id] = fieldLinks[id];
        }
      }
      return [mergedFields, mergedFieldLinks];
    },
    [{}, {}],
  );
  const [mergedFields, mergedFieldLinks] = merged;

  // add new placeholder fields that do not match new placeholder fields
  const currentPlaceholderSet = Object.values(fields).reduce<Set<string>>(
    (acc, field) => {
      const { placeholder } = field;
      if (placeholder) {
        acc.add(placeholder);
      }
      return acc;
    },
    new Set(),
  );
  Object.keys(newPlaceholderFieldsMapping).forEach((placeholder) => {
    if (!currentPlaceholderSet.has(placeholder)) {
      const matchingPlaceholderFields =
        newPlaceholderFieldsMapping[placeholder];
      matchingPlaceholderFields.forEach((newField) => {
        mergedFields[newField.id] = newField;
      });
    }
  });

  return [mergedFields, mergedFieldLinks];
}

export function isLatestVersion(workflow: {
  versions: { number: number }[];
  versionNumber: number;
}) {
  const latestVersion = Math.max(
    ...workflow.versions.map((version) => version.number),
  );
  return workflow.versionNumber === latestVersion;
}

// TODO: product to confirm behaviors for missing/unamed fields to be consistently applied in the UI.
export function getFieldDisplayName(field?: { name?: string | null } | null) {
  if (!field) {
    return 'Missing field';
  } else if (!field.name) {
    return 'Unnamed field';
  } else {
    return field.name;
  }
}

export function pathObjectToString(
  path: { type: string; value: string }[],
  fields: Dict<{ name?: string | null }>,
) {
  return path
    .map((token) => {
      if (token.type === 'field') {
        return getFieldDisplayName(fields[token.value]);
      }
      return token.value;
    })
    .join('');
}

// the newly created ID for a condition rule is explicitly set to `null` because it can be updated later
export const createConditionRule = () => ({
  id: null,
  satisfiedWhenCondMet: true,
  enabledWhenSatisfied: true,
});

// to check if every counterparty signer has both questions for name and email fields,
// we can just check if every counterparty signer name or email field has linked questions
export function testLackCounterpartySignerQuestion(workflow: {
  form: { sections: { questions: { fieldId: string }[] }[] };
  fieldLinks: Dict<DraftFieldLink>;
  parties: Dict<{ type: WorkflowPartyType | string }>;
  signers: Dict<DraftSigner>;
}) {
  const { form, fieldLinks, parties, signers } = workflow;
  const { sections } = form;

  const counterpartySignerIds = Object.values(signers)
    .filter(
      (signer) =>
        parties[signer.partyId].type === WorkflowPartyType.Counterparty,
    )
    .map((signer) => signer.id);

  const requiredFieldTypes = ['name', 'email'];
  const requiredFieldIds: string[] = [];
  for (const [key, value] of Object.entries(fieldLinks)) {
    if (
      counterpartySignerIds.includes(value.id as string) &&
      requiredFieldTypes.includes(value.key ?? '')
    ) {
      requiredFieldIds.push(key);
    }
  }

  const questions = sections.map((section) => section.questions).flat();

  const questionFieldIds = Object.values(questions).map(
    (question) => question.fieldId,
  );

  return requiredFieldIds.some(
    (requiredFieldId) => !questionFieldIds.includes(requiredFieldId),
  );
}

/**
 * Returns a joined and lowercased file extension (usable in FileInput.accept) given workflow.acceptedFileTypes.  Returns the default file extensions if no file types are specified.
 * @param {object} workflow
 *    Partially typed object to aid usage of util.
 * @param {string[]} workflow.acceptedFileTypes
 *    An array of file extensions (`.`-inclusive)
 * @param {FileExtensionType[]} [defaultFileExtensions]
 *    An array of file extensions (`.`-inclusive)
 * @param {FileExtensionType[]} [requiredFileExtensions]
 *    An array of required file extensions to always include.
 * @returns {string}
 *    Joined file extension (`.`-inclusive)
 */
export function getAcceptedFiles(
  workflow: { acceptedFileTypes?: string[] },
  defaultFileExtensions: string[] = [
    FileExtensionType.Doc,
    FileExtensionType.Docx,
  ],
  requiredFileExtensions: string[] = [],
) {
  const { acceptedFileTypes } = workflow;
  const fileExtensions =
    acceptedFileTypes!.length > 0 ? acceptedFileTypes! : defaultFileExtensions;
  return [...fileExtensions, ...requiredFileExtensions]
    .map((x) => '.' + coerceFileType(x).toLowerCase())
    .join(', ');
}

export const DefaultCustomSettings = {
  defaultValue: {},
  options: [],
};

export function getDefaultCustomSettingsByFieldType(
  fieldType: WorkflowFieldType,
) {
  const customSettings: Dict = { ...DefaultCustomSettings };

  const customSettingKey: Dict<string> =
    customSettingsKeys[fieldType as keyof typeof customSettingsKeys] ?? {};
  const customSettingsOptionValue: Dict<Dict<string>> =
    customSettingsOptionValues[
      fieldType as keyof typeof customSettingsOptionValues
    ] ?? {};
  const defaultValue: Dict = {};

  switch (fieldType) {
    case WorkflowFieldType.Address: {
      customSettings[customSettingKey.DOCUMENT_PLACEMENT] =
        customSettingsOptionValue.DOCUMENT_PLACEMENT.SINGLE_LINE;
      defaultValue[valueKeys[fieldType].COUNTRY] = 'United States';
      break;
    }
    case WorkflowFieldType.Date: {
      customSettings[customSettingKey.FORMAT] = DateFormatType.Long;
      break;
    }
    case WorkflowFieldType.Department:
    case WorkflowFieldType.User: {
      customSettings[customSettingKey.RULE] =
        customSettingsOptionValue.RULE.ALL;
      break;
    }
    case WorkflowFieldType.Email: {
      customSettings[customSettingKey.EMAIL_TYPE] =
        customSettingsOptionValue.EMAIL_TYPE.NONE;
      break;
    }
    case WorkflowFieldType.File: {
      customSettings[customSettingKey.FILE_LIMIT] =
        customSettingsOptionValue.FILE_LIMIT.UNLIMITED;
      customSettings[customSettingKey.ALLOWED_FILE_TYPE_RULE] =
        customSettingsOptionValue.FILE_TYPE_RULE.ANY;
      break;
    }
    case WorkflowFieldType.SingleSelect: {
      customSettings[customSettingKey.ENABLE_CREATE_OPTIONS] = true;
      break;
    }
    case WorkflowFieldType.MonetaryValue: {
      customSettings[customSettingKey.CURRENCY_RULE] =
        customSettingsOptionValue.CURRENCY_RULE.ANY;
      defaultValue[valueKeys[fieldType].CURRENCY] = 'USD';
      break;
    }
    case WorkflowFieldType.TimePeriod: {
      customSettings.options = getTimePeriodFieldDefaultOptions();
      break;
    }
    default:
      break;
  }

  return { ...customSettings, defaultValue };
}
